import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import CustomSelect from '../ui/CustomSelect';
import { createBot } from '../../api/botsApi';
import { fetchAccounts } from '../../api/api';

const BotForm = ({ bot, onSuccess }) => {
  const navigate = useNavigate();
  const [name, setName] = useState(bot ? bot.name : '');
  const [accountId, setAccountId] = useState(bot ? bot.account_id : '');
  // Removed strategyTemplateFilename state as it's no longer needed for new bot creation
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);


  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoadingAccounts(true);
      let formError = null;

      try {
        const accountsData = await fetchAccounts();
        setAccounts(accountsData.map(acc => ({ value: acc.account_id, label: `${acc.name} (${acc.account_id})` })));
      } catch (err) {
        console.error("Failed to fetch accounts for bot form:", err);
        formError = (formError ? formError + "\n" : "") + "Failed to load accounts.";
      } finally {
        setIsLoadingAccounts(false);
      }
      if (formError) setError(formError + " Please try again.");
    };
    loadInitialData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const botData = {
      name,
      account_id: accountId,
      // strategy_template is removed as per new modular approach for bot creation
      // created_by_id is set by backend
    };

    try {
      // TODO: Implement updateBot logic if 'bot' prop is provided
      if (bot) {
        // await updateBot(bot.id, botData); // Assuming updateBot exists and works similarly
        console.log("Update functionality to be implemented.");
      } else {
        await createBot(botData);
      }
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/bots'); // Default redirect
      }
    } catch (err) {
      console.error("Failed to save bot:", err);
      setError(err.message || `Failed to save bot. ${err.detail || ''}`);
    } finally {
      setIsLoading(false);
    }
  };

  const accountOptions = isLoadingAccounts
    ? [{ value: '', label: 'Loading accounts...' }]
    : accounts.length > 0
      ? accounts
      : [{ value: '', label: 'No accounts available', isDisabled: true }]; // Add a disabled placeholder if no accounts

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <p className="text-destructive whitespace-pre-line">{error}</p>}
      <div>
        <label htmlFor="botName" className="block text-sm font-medium text-textPrimary">Bot Name</label>
        <Input
          id="botName"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="My Awesome Bot"
          required
          className="mt-1"
        />
      </div>
      <div>
        <label htmlFor="accountId" className="block text-sm font-medium text-textPrimary">Trading Account</label>
        <CustomSelect
          id="accountId"
          value={accountId} // Pass the primitive value directly
          onChange={(selectedValue) => setAccountId(selectedValue)} // onChange receives the primitive value
          options={accountOptions}
          placeholder="Select an account"
          required
          className="mt-1"
        />
      </div>
      {/* Removed Strategy Template selection as it's handled by BotVersionForm */}
      <div className="flex justify-end space-x-3">
        <Button type="button" variant="secondary" onClick={() => navigate('/bots')}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? (bot ? 'Updating...' : 'Creating...') : (bot ? 'Update Bot' : 'Create Bot')}
        </Button>
      </div>
    </form>
  );
};

export default BotForm;
